import type {
	Interactions,
	InteractionReactions,
	Transitions,
	Styles,
	VariantId,
	ReactionTypes,
	TriggerType,
	ModelData,
	InteractionsConfig,
	EffectId,
	ResolvedVariantRelation,
	AnimationOptions,
	InteractionEffects,
	RangeOffset,
	EffectWithReactionType,
	Spx,
	VariantIdToStyleProperties,
	TransformationsFromCarmi,
	EffectOptionsFromCarmi,
	Effect,
	EffectRef,
	ResolvedEffectRefs,
	AnimationTriggerParams,
	TransitionMethod,
	TriggerParams,
} from '@wix/thunderbolt-becky-types'
import { SkinDefaultsData } from './themeData/skinsDefaultProperties'
import { getStyleCssVars } from './themeData/themeDataCss'
import { getCSSObjectFromTransformationItem } from './transformationsFunctionLibrary'
import { BeckyModel } from '../types'

const STATE_METHODS: { [key in ReactionTypes]?: TransitionMethod } = {
	ToggleState: 'toggle',
	AddState: 'add',
	RemoveState: 'remove',
	RemoveAllStates: 'clear',
}

const KEBAB_TO_CAMEL_MAP: { [key: string]: TriggerType } = {
	click: 'click',
	hover: 'hover',
	'viewport-enter': 'viewEnter',
	'view-progress': 'viewProgress',
	'animation-end': 'animationEnd',
	'pointer-move': 'pointerMove',
	'page-visible': 'pageVisible',
}

const ANIMATION_PROPERTIES_MAP: { [key: string]: string } = {
	startOffset: 'rangeStart',
	endOffset: 'rangeEnd',
}

const omitProperties = (props: Array<string>, obj: Record<string, any>) => {
	return Object.keys(obj).reduce((acc, key) => {
		if (!props.includes(key)) {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				acc[key] = omitProperties(['id', 'metaData'], obj[key])
			} else {
				acc[key] = obj[key]
			}
		}
		return acc
	}, {} as Record<string, any>)
}

const buildStyleProperties = (propertiesObject: { [p: string]: string | number }) => {
	return Object.entries(propertiesObject).map(([name, value]) => ({ name, value: `${value}` }))
}

const transformationFixer = (transformationsFromCarmi: TransformationsFromCarmi): VariantIdToStyleProperties => {
	return Object.entries(transformationsFromCarmi).reduce((acc: VariantIdToStyleProperties, [key, value]) => {
		acc[key as VariantId] = buildStyleProperties(
			// https://wix.atlassian.net/browse/TB-11438 -- passing empty array because ignored transform properties are not supported right now
			getCSSObjectFromTransformationItem(value.transformationData, [], value.rotationInDegrees)
		)

		return acc
	}, {})
}

const omitTypePropertyFromEffects = (effects: Array<EffectWithReactionType>) => {
	return effects.map(({ type: _, ...rest }) => rest)
}

const stylesFixer = (
	styles: Styles,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	siteColors: Array<string>,
	componentsSkinParams?: Record<string, SkinDefaultsData>
): VariantIdToStyleProperties => {
	return Object.entries(styles).reduce((acc: Record<string, any>, [key, value]) => {
		const cssVars = getStyleCssVars(value, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
		acc[key] = buildStyleProperties(cssVars)
		return acc
	}, {})
}

const animationOptionsFixer = (effectOptions: AnimationOptions) => {
	return Object.entries(effectOptions).reduce((acc: Record<string, any>, [key, value]) => {
		if (key === 'startOffset' || key === 'endOffset') {
			const offset = value as RangeOffset
			acc[ANIMATION_PROPERTIES_MAP[key]] = {
				name: offset?.name,
				offset: offset?.offset?.value,
				type: offset?.offset?.type,
			}
		} else if (key === 'iterations' && value === 0) {
			acc[key] = 'Infinity'
		} else {
			acc[key] = value
		}
		return acc
	}, {})
}

const effectsFixer = (effectIdToRefArray: Record<EffectId, EffectOptionsFromCarmi>) => {
	const propertiesToRemove = ['id', 'type', 'metaData', 'allowReplay']
	return (Object.keys(effectIdToRefArray) as Array<EffectId>).reduce((acc: InteractionEffects, key) => {
		const variantRelation = effectIdToRefArray[key].find((element) => element.type === 'VariantRelation') as
			| ResolvedVariantRelation<AnimationOptions>
			| undefined

		acc[key] = animationOptionsFixer(
			(variantRelation?.to
				? omitProperties(propertiesToRemove, variantRelation.to)
				: (omitProperties(
						propertiesToRemove,
						effectIdToRefArray[key]?.[0]
				  ) as AnimationOptions)) as AnimationOptions
		)

		return acc
	}, {})
}

const toMilliseconds = (number: number) => number * 1000

const convertTransitionDurations = (transitions: Transitions) => {
	return Object.entries(transitions).reduce((acc: Transitions, [key, value]) => {
		const { duration, ...rest } = value
		acc[key as VariantId] = {
			...(duration && { duration: toMilliseconds(duration) }),
			...rest,
		}
		return acc
	}, {})
}

const collectReactions = (reactionObjects: Array<InteractionReactions>) => {
	return reactionObjects.reduce((acc, reactionObject) => {
		const [variantId] = Object.keys(reactionObject) as Array<VariantId>
		const reactions = reactionObject[variantId]
		if (!acc[variantId]) {
			acc[variantId] = []
		}
		acc[variantId] = acc[variantId].concat(reactions)
		return acc
	}, {})
}

const getState = (
	stateVariantId: VariantId,
	transformations: VariantIdToStyleProperties,
	transitions: Transitions,
	styles: VariantIdToStyleProperties
) => {
	return {
		transition: {
			styleProperties: [...(styles[stateVariantId] || []), ...(transformations[stateVariantId] || [])],
			...transitions[stateVariantId],
		},
	}
}

const getEffectsWithReactionType = (variantRelationId: VariantId, data: ModelData): Array<EffectWithReactionType> => {
	const effects: Array<EffectWithReactionType> = []
	const variantRelationReactions = data.reactions[variantRelationId]

	if (!variantRelationReactions) {
		return []
	}

	variantRelationReactions.forEach((reaction) => {
		effects.push({
			target: `#${reaction.target}`,
			type: reaction.type,
			...(reaction.state && getState(reaction.state, data.transformations, data.transitions, data.styles)),
			...(reaction.effect && data.effects[reaction.effect]),
		})
	})

	return effects
}

const getEffectRefs = (variantRelationId: VariantId, data: ModelData) => {
	const effectRefs: Array<EffectRef> = []
	const variantRelationReactions = data.reactions[variantRelationId]
	const resolvedEffectRefs: Record<VariantId | EffectId, Effect> = {}
	if (!variantRelationReactions) {
		return { effectRefs: [], resolvedEffectRefs: {} }
	}
	variantRelationReactions.forEach((reaction) => {
		const id = reaction.state || reaction.effect
		const resolvedEffectRef = {
			target: `#${reaction.target}`,
			...(reaction.state && getState(reaction.state, data.transformations, data.transitions, data.styles)),
			...(reaction.effect && data.effects[reaction.effect]),
		}
		if (id) {
			resolvedEffectRefs[id] = resolvedEffectRef
			effectRefs.push({
				target: `#${reaction.target}`,
				effectId: id,
			})
		}
	})
	return { effectRefs, resolvedEffectRefs }
}

const buildInteractionsAndEffectRefs = (
	data: ModelData
): { interactions: Interactions; globalResolvedEffectRefs: ResolvedEffectRefs } => {
	if (!data.triggers) {
		return { interactions: [], globalResolvedEffectRefs: {} }
	}

	const interactions: Interactions = []
	const globalResolvedEffectRefs: ResolvedEffectRefs = {}

	data.triggers.forEach((interactionTrigger) => {
		if (!KEBAB_TO_CAMEL_MAP.hasOwnProperty(interactionTrigger.trigger)) {
			return
		}

		if (interactionTrigger.params) {
			if (interactionTrigger.trigger === 'animation-end') {
				const { effectRefs, resolvedEffectRefs } = getEffectRefs(interactionTrigger.id, data)
				Object.assign(globalResolvedEffectRefs, resolvedEffectRefs)
				const triggerParams = interactionTrigger.params as AnimationTriggerParams
				interactions.push({
					source: `#${interactionTrigger.componentId}`,
					trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
					...(triggerParams.effect && {
						params: {
							effectId: triggerParams.effect,
						},
					}),
					effects: effectRefs,
				})
			} else {
				const effectsWithReactionType = getEffectsWithReactionType(interactionTrigger.id, data)
				const params = interactionTrigger.params.hasOwnProperty('threshold')
					? { ...interactionTrigger.params, type: 'once' as const }
					: (interactionTrigger.params as TriggerParams)

				interactions.push({
					source: `#${interactionTrigger.componentId}`,
					trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
					...(Object.keys(params).length && { params }),
					effects: omitTypePropertyFromEffects(effectsWithReactionType),
				})
			}
		} else if (interactionTrigger.trigger === 'view-progress') {
			const effectsWithReactionType = getEffectsWithReactionType(interactionTrigger.id, data)
			interactions.push({
				source: `#${interactionTrigger.componentId}`,
				trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
				effects: omitTypePropertyFromEffects(effectsWithReactionType),
			})
		} else {
			const effectsWithReactionType = getEffectsWithReactionType(interactionTrigger.id, data)
			const stateEffectsMap = effectsWithReactionType.reduce((acc, currEffect) => {
				if (!acc[currEffect.type]) {
					acc[currEffect.type] = []
				}
				acc[currEffect.type].push(currEffect)
				return acc
			}, {} as { [key: string]: Array<EffectWithReactionType> })

			Object.entries(STATE_METHODS).forEach(([stateType, method]) => {
				if (stateEffectsMap[stateType] && stateEffectsMap[stateType].length > 0) {
					interactions.push({
						source: `#${interactionTrigger.componentId}`,
						trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
						params: interactionTrigger.trigger === 'hover' ? { method: 'toggle' } : { method },
						effects: omitTypePropertyFromEffects(stateEffectsMap[stateType]),
					})
				}
			})
		}
	})

	return { interactions, globalResolvedEffectRefs }
}

const buildConfig = (data: {
	interactions: Interactions
	globalResolvedEffectRefs: ResolvedEffectRefs
}): InteractionsConfig => {
	return {
		effects: data.globalResolvedEffectRefs,
		conditions: {},
		interactions: data.interactions,
	}
}

// TODO: this is just a naive merge, need to figure out what are the 'source' and 'target' properties in the manifest
const mergeBuilderComponentsInteractions = (
	interactionsConfig: InteractionsConfig,
	builderComponentsInteractionsMap: BeckyModel['builderComponentsInteractions']
): InteractionsConfig => {
	if (!builderComponentsInteractionsMap) {
		return interactionsConfig
	}
	return Object.values(builderComponentsInteractionsMap).reduce((acc, componentConfig) => {
		acc.effects = { ...acc.effects, ...componentConfig.effects }
		acc.conditions = { ...acc.conditions, ...componentConfig.conditions }
		acc.interactions = [...acc.interactions, ...(componentConfig.interactions || [])]
		return acc
	}, interactionsConfig)
}

export {
	buildInteractionsAndEffectRefs,
	buildConfig,
	collectReactions,
	effectsFixer,
	stylesFixer,
	transformationFixer,
	convertTransitionDurations,
	mergeBuilderComponentsInteractions,
}
